import { useMemo } from 'react';
import { typography } from '@/shared/theme/tokens';
import { Sparkline } from './Sparkline';
import { LIVE_STATUS_META, computeStatus, TERM } from '../liveModeMeta';
import { Pane, PaneHeader } from './Pane';

const MONO = typography.fontFamily.mono;
const r1 = (n) => Math.round(n * 10) / 10;

function pickMetrics(alerts, max = 2) {
  const byName = new Map();
  for (const a of alerts) if (!byName.has(a.parameterName)) byName.set(a.parameterName, a);
  const all = [...byName.values()];
  const targets = all.filter((a) => a.paramType === 'target');
  return [...targets, ...all.filter((a) => a.paramType !== 'target')].slice(0, max);
}

// Fraction of buffered samples that sat inside the optimal band — session-derived
// (live buffers, not a real 30-day aggregate), hence the explicit "Last 12h".
function inBandPct(alerts) {
  let total = 0; let inBand = 0;
  for (const a of alerts) for (const v of a.buffer ?? []) { total += 1; if (computeStatus(v, a) === 'ok') inBand += 1; }
  return total ? Math.round((inBand / total) * 100) : null;
}

/**
 * Shift-handoff readout — "how did the plant sit this window". Surfaces handoff
 * memory, not a dashboard. All figures session-derived from the live buffers +
 * current snapshot; window is a rolling "Last 12h" (no real shift roster).
 */
export function PerformanceStats({ alerts = [], recs = [] }) {
  const metrics = useMemo(() => pickMetrics(alerts), [alerts]);
  const { pct, activeAlerts, pending, awaiting } = useMemo(() => ({
    pct: inBandPct(alerts),
    activeAlerts: alerts.filter((a) => !a.isStale && a.status !== 'ok').length,
    pending: recs.filter((r) => r.status === 'unacknowledged').length,
    awaiting: recs.filter((r) => r.status === 'accepted').length,
  }), [alerts, recs]);

  return (
    <Pane>
      <PaneHeader
        title="Session"
        accent={TERM.ok}
        right={<span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.wide, color: TERM.inkFaint }}>LAST 12H</span>}
      />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* readout strip */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${TERM.line}` }}>
          <Readout label="In band" value={pct == null ? '—' : `${pct}%`} color={TERM.ok} />
          <Readout label="Active" value={activeAlerts} color={activeAlerts ? TERM.crit : TERM.ink} divider />
          <Readout label="Awaiting" value={awaiting} color={awaiting ? TERM.info : TERM.ink} divider />
        </div>
        <div style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          padding: '7px 12px', borderBottom: `1px solid ${TERM.line}`,
        }}>
          {pending} REC{pending === 1 ? '' : 'S'} PENDING · SESSION-DERIVED
        </div>

        {/* metric tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: metrics.length > 1 ? '1fr 1fr' : '1fr' }}>
          {metrics.map((m, i) => {
            const peak = m.buffer.length ? r1(Math.max(...m.buffer)) : m.liveValue;
            const avg = m.buffer.length ? r1(m.buffer.reduce((s, v) => s + v, 0) / m.buffer.length) : m.liveValue;
            const meta = LIVE_STATUS_META[m.status] ?? LIVE_STATUS_META.ok;
            return (
              <div key={m.id} style={{
                padding: '11px 12px',
                borderBottom: `1px solid ${TERM.line}`,
                borderLeft: i % 2 === 1 ? `1px solid ${TERM.line}` : 'none',
              }}>
                <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkDim, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.parameterName}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                  <span style={{ fontFamily: MONO, fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: TERM.ink, fontVariantNumeric: 'tabular-nums' }}>{peak}</span>
                  {m.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint }}>{m.unit}</span>}
                  <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: meta.color, marginLeft: 2 }}>PEAK</span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Sparkline data={m.buffer} color={meta.color} height={22} animate={false} />
                </div>
                <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint, fontVariantNumeric: 'tabular-nums', marginTop: 3 }}>
                  AVG {avg}{m.unit ? ` ${m.unit}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Pane>
  );
}

function Readout({ label, value, color, divider }) {
  return (
    <div style={{ flex: 1, padding: '10px 12px', borderLeft: divider ? `1px solid ${TERM.line}` : 'none' }}>
      <div style={{ fontFamily: MONO, fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.bold, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}
